import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../services/supabaseClient'
import { useAuth } from '../context/AuthContext'

const slotOptions = [
  { value: 'morning_meeting', label: 'اللقاء الصباحي' },
  { value: 'learning_centers', label: 'الأركان التعليمية' },
  { value: 'movement', label: 'النشاط الحركي' },
  { value: 'other', label: 'أنشطة أخرى' },
]

function slotLabel(value) {
  const found = slotOptions.find(s => s.value === value)
  return found ? found.label : value || '—'
}

function levelLabel(level) {
  if (level === 'prekg') return 'مرحلة الحضانة'
  if (level === 'kg1') return 'صف الروضة (الأول)'
  if (level === 'kg2') return 'صف التمهيدي (الثاني)'
  return level || '—'
}

export default function DailyPlan() {
  const { user, signOut } = useAuth()
  const email = user?.email || ''
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [rows, setRows] = useState([])
  const navigate = useNavigate()

  useEffect(() => {
    let active = true
    ;(async () => {
      setLoading(true)
      setError('')
      try {
        const { data, error: rpcError } = await supabase.rpc('get_kg_kid_daily_plan', {
          p_date: date,
        })
        if (!active) return
        if (rpcError) {
          setError(rpcError.message || 'حدث خطأ أثناء تحميل خطة اليوم')
          setRows([])
        } else {
          setRows(data || [])
        }
      } catch (e) {
        if (!active) return
        setError(e.message || 'تعذر تحميل خطة اليوم')
        setRows([])
      } finally {
        if (!active) return
        setLoading(false)
      }
    })()
    return () => { active = false }
  }, [date])

  const planInfo = rows.length > 0 ? rows[0] : null
  const activities = rows.filter(r => r.activity_title)

  const groupedActivities = activities.reduce((acc, r) => {
    const key = r.slot_type || 'other'
    if (!acc[key]) acc[key] = []
    acc[key].push(r)
    return acc
  }, {})

  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        padding: '1.5rem',
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: 16,
            padding: 16,
            boxShadow: '0 10px 25px rgba(15,23,42,0.08)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ margin: 0 }}>خطة اليوم - رياض الأطفال</h1>
            <button
              type="button"
              onClick={signOut}
              style={{
                padding: '6px 10px',
                borderRadius: 999,
                border: '1px solid #e5e7eb',
                background: '#f9fafb',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              تسجيل الخروج
            </button>
          </div>

          <div style={{ color: '#6b7280', marginTop: 4, marginBottom: 12, fontSize: 14 }}>
            الاطلاع على خطة اليوم والأنشطة المخططة لطفلك في رياض الأطفال.
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => navigate('/')}
                style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}
              >
                لوحة المعلومات
              </button>
              <button
                type="button"
                onClick={() => navigate('/attendance')}
                style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}
              >
                الحضور
              </button>
              <button
                type="button"
                disabled
                style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #2563eb', background: '#2563eb', color: '#fff', cursor: 'default', fontSize: 13 }}
              >
                خطة اليوم
              </button>
              <button
                type="button"
                onClick={() => navigate('/assessment')}
                style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}
              >
                التقييم
              </button>
              <button
                type="button"
                onClick={() => navigate('/fees')}
                style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}
              >
                الرسوم
              </button>
            </div>
          </div>

          {email && (
            <p style={{ marginBottom: 8 }}>تم تسجيل الدخول بالبريد: <strong>{email}</strong></p>
          )}

          <div
            style={{
              marginBottom: 12,
              padding: 12,
              borderRadius: 12,
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              fontSize: 14,
            }}
          >
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 13, color: '#374151' }}>
                تاريخ الخطة
                <input
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  style={{
                    marginInlineStart: 8,
                    padding: '6px 8px',
                    borderRadius: 8,
                    border: '1px solid #d1d5db',
                  }}
                />
              </label>
            </div>
            {planInfo ? (
              <>
                <div style={{ marginBottom: 4 }}>
                  التاريخ: <strong>{String(planInfo.plan_date)}</strong>
                </div>
                <div style={{ marginBottom: 4 }}>
                  وحدة الخبرة:
                  {' '}
                  <strong>{planInfo.unit_name || 'لم تُحدد بعد'}</strong>
                  {planInfo.unit_level && (
                    <>
                      {' '}
                      (المرحلة: {levelLabel(planInfo.unit_level)})
                    </>
                  )}
                </div>
                {planInfo.unit_description && (
                  <div style={{ marginTop: 4, fontSize: 13, color: '#4b5563' }}>
                    وصف الوحدة: {planInfo.unit_description}
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                لا توجد خطة مسجلة لهذا اليوم حتى الآن.
              </div>
            )}
          </div>

          {loading && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>جاري تحميل خطة اليوم...</div>
          )}

          {!loading && error && (
            <div style={{ color: '#b91c1c', marginTop: 16 }}>{error}</div>
          )}

          {!loading && !error && planInfo && (
            <div style={{ display: 'grid', gap: 12 }}>
              {slotOptions.map(slot => {
                const acts = groupedActivities[slot.value] || []
                return (
                  <div
                    key={slot.value}
                    style={{
                      borderRadius: 12,
                      border: '1px solid #e5e7eb',
                      padding: 12,
                      background: '#fff',
                    }}
                  >
                    <h3 style={{ margin: '0 0 8px 0', fontSize: 15 }}>{slot.label}</h3>
                    {acts.length === 0 ? (
                      <div style={{ fontSize: 13, color: '#6b7280' }}>
                        لا توجد أنشطة مسجلة في هذه الفترة لهذا اليوم.
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: 8 }}>
                        {acts.map((act, idx) => (
                          <div
                            key={`${act.activity_title}-${idx}`}
                            style={{
                              borderRadius: 10,
                              border: '1px solid #e5e7eb',
                              padding: 8,
                              background: '#f9fafb',
                            }}
                          >
                            <div style={{ fontSize: 14, fontWeight: 500, color: '#111827' }}>
                              {act.activity_title}
                            </div>
                            {act.activity_description && (
                              <div style={{ fontSize: 13, color: '#4b5563', marginTop: 2 }}>
                                {act.activity_description}
                              </div>
                            )}
                            {act.teaching_aids && (
                              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                                الوسائل التعليمية: {act.teaching_aids}
                              </div>
                            )}
                            {(act.start_time || act.end_time) && (
                              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                                الوقت:
                                {' '}
                                {act.start_time || '—'}
                                {' '}
                                إلى
                                {' '}
                                {act.end_time || '—'}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
